import { slugify } from '~/utils/slugify'

/** Lien menu : libellé + slug optionnel (string brute ou objet JSON / API). */
export type MegaMenuLinkInput = string | { title: string; link?: string }

/** Lien menu normalisé. */
export interface MegaMenuLinkItem {
  title: string
  link: string
}

export interface MegaMenuCity {
  city: string
  country: string
}

export interface MegaMenuDiploma {
  label: string
  level: string
}

export interface MegaMenuArticle {
  title: string
  link: string
  readTime: string
}

/** Fragment JSON / API `/api/mega-menu`. */
export interface MegaMenuPayload {
  featuredSchools?: MegaMenuLinkInput[] | null
  schoolTypes?: string[] | null
  popularCities?: MegaMenuCity[] | null
  trendCities?: MegaMenuCity[] | null
  jobSectors?: MegaMenuLinkInput[] | null
  diplomas?: MegaMenuDiploma[] | null
  popularArticles?: MegaMenuArticle[] | null
}

export interface MegaMenuNormalized {
  featuredSchools: MegaMenuLinkItem[]
  schoolTypes: string[]
  popularCities: MegaMenuCity[]
  trendCities: MegaMenuCity[]
  jobSectors: MegaMenuLinkItem[]
  diplomas: MegaMenuDiploma[]
  popularArticles: MegaMenuArticle[]
}

/** État avant chargement du JSON public — le contenu vient uniquement du fallback + overrides API. */
export const EMPTY_MEGA_MENU: MegaMenuNormalized = {
  featuredSchools: [],
  schoolTypes: [],
  popularCities: [],
  trendCities: [],
  jobSectors: [],
  diplomas: [],
  popularArticles: [],
}

export function cloneMegaMenu(menu: MegaMenuNormalized): MegaMenuNormalized {
  return JSON.parse(JSON.stringify(menu)) as MegaMenuNormalized
}

function isNonEmptyArray<T>(value: T[] | null | undefined): value is T[] {
  return Array.isArray(value) && value.length > 0
}

function normalizeLinkItems(entries: MegaMenuLinkInput[] | null | undefined): MegaMenuLinkItem[] {
  if (!isNonEmptyArray(entries)) {
    return []
  }

  return entries
    .map((item: MegaMenuLinkInput): MegaMenuLinkItem | null => {
      if (typeof item === 'string') {
        const title: string = item.trim()
        if (!title) {
          return null
        }
        return { title, link: slugify(title) }
      }

      const title: string = item.title?.trim() ?? ''
      if (!title) {
        return null
      }

      const link: string =
        typeof item.link === 'string' && item.link.trim() !== '' ? item.link.trim() : slugify(title)
      return { title, link }
    })
    .filter((item: MegaMenuLinkItem | null): item is MegaMenuLinkItem => item !== null)
}

function normalizeArticles(list: MegaMenuArticle[] | null | undefined): MegaMenuArticle[] {
  if (!isNonEmptyArray(list)) {
    return []
  }

  return list
    .filter((article: MegaMenuArticle): boolean =>
      Boolean(article.link?.trim() && article.title?.trim()),
    )
    .map(
      (article: MegaMenuArticle): MegaMenuArticle => ({
        title: article.title.trim(),
        link: article.link.trim(),
        readTime:
          typeof article.readTime === 'string' && article.readTime.trim() !== ''
            ? article.readTime.trim()
            : '—',
      }),
    )
}

function normalizeCities(list: MegaMenuCity[] | null | undefined): MegaMenuCity[] {
  if (!isNonEmptyArray(list)) {
    return []
  }

  return list.filter((city: MegaMenuCity): boolean =>
    Boolean(city.city?.trim() && city.country?.trim()),
  )
}

function normalizeStrings(list: string[] | null | undefined): string[] {
  if (!isNonEmptyArray(list)) {
    return []
  }

  return list
    .map((entry: string): string => entry.trim())
    .filter((entry: string): boolean => entry.length > 0)
}

function normalizeDiplomas(list: MegaMenuDiploma[] | null | undefined): MegaMenuDiploma[] {
  if (!isNonEmptyArray(list)) {
    return []
  }

  return list
    .filter((diploma: MegaMenuDiploma): boolean => Boolean(diploma.label?.trim()))
    .map(
      (diploma: MegaMenuDiploma): MegaMenuDiploma => ({
        label: diploma.label.trim(),
        level: typeof diploma.level === 'string' ? diploma.level.trim() : '',
      }),
    )
}

/**
 * Fusionne une clé du payload si la normalisation produit un tableau non vide.
 * @template TInput Type des entrées brutes.
 * @template K Clé cible dans `MegaMenuNormalized`.
 * @param {MegaMenuNormalized} next Menu en cours de fusion.
 * @param {K} key Clé à mettre à jour.
 * @param {TInput[] | null | undefined} raw Valeur brute JSON / API.
 * @param {(items: TInput[]) => MegaMenuNormalized[K]} normalize Fonction de normalisation.
 */
function mergeMenuArray<K extends keyof MegaMenuNormalized, TInput>(
  next: MegaMenuNormalized,
  key: K,
  raw: TInput[] | null | undefined,
  normalize: (items: TInput[]) => MegaMenuNormalized[K],
): void {
  if (!isNonEmptyArray(raw)) {
    return
  }

  const normalized: MegaMenuNormalized[K] = normalize(raw)
  if (normalized.length === 0) {
    return
  }

  next[key] = normalized
}

/**
 * Fusionne un `MegaMenuPayload` dans un menu normalisé : chaque tableau non vide remplace la clé correspondante ;
 * omis / `null` / `[]` gardent les valeurs de `base` ; normalisation vide → conserve `base`.
 * @param {MegaMenuNormalized} base Menu de départ (`EMPTY_MEGA_MENU` ou déjà fusionné depuis le JSON).
 * @param {MegaMenuPayload | null | undefined} raw JSON et/ou fragment API (`MegaMenuPayload`).
 * @returns {MegaMenuNormalized} Nouveau menu fusionné.
 */
export function mergeFullMegaMenu(
  base: MegaMenuNormalized,
  raw: MegaMenuPayload | null | undefined,
): MegaMenuNormalized {
  if (!raw) {
    return base
  }

  const next: MegaMenuNormalized = cloneMegaMenu(base)

  mergeMenuArray(next, 'featuredSchools', raw.featuredSchools, normalizeLinkItems)
  mergeMenuArray(next, 'schoolTypes', raw.schoolTypes, normalizeStrings)
  mergeMenuArray(next, 'popularCities', raw.popularCities, normalizeCities)
  mergeMenuArray(next, 'trendCities', raw.trendCities, normalizeCities)
  mergeMenuArray(next, 'jobSectors', raw.jobSectors, normalizeLinkItems)
  mergeMenuArray(next, 'diplomas', raw.diplomas, normalizeDiplomas)
  mergeMenuArray(next, 'popularArticles', raw.popularArticles, normalizeArticles)

  return next
}

export const DEFAULT_MEGA_MENU_FALLBACK_FILE = 'mega-menu-fallback.json'
